import { types, flow, getSnapshot } from 'mobx-state-tree';
import { ApiStore } from './ApiStore';
import { CompanyBackground, NullCompanyBackground } from './CompanyBackground';
import { ProspectProfile } from './ProspectProfile';
import { TopCompetitor } from './TopCompetitor';
import { CompanyOffering } from './CompanyOffering';
import axios from 'axios';
import { QuestionAnswer } from './QuestionAnswer';

// Per-company Training Plan Looms. Only slots with a plausible bare Loom id
// survive (LoomEmbed builds the iframe src as loom.com/embed/{id}, so the field
// can never point a customer-facing iframe at an arbitrary URL), and a malformed
// admin write degrades to "no video" instead of crashing company load (the
// release-1.8.6 prospect-profile failure class).
const TRAINING_PLAN_VIDEO_SLOTS = ['intro', 'elevatorPitch', 'casualEncounter'];
const LOOM_ID_RE = /^[a-zA-Z0-9]{16,64}$/;
const sanitizeTrainingPlanVideos = (raw) => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out = {};
  TRAINING_PLAN_VIDEO_SLOTS.forEach((slot) => {
    const v = raw[slot];
    if (v && typeof v === 'object' && typeof v.loomId === 'string' && LOOM_ID_RE.test(v.loomId.trim())) {
      out[slot] = {
        loomId: v.loomId.trim(),
        ...(typeof v.title === 'string' ? { title: v.title } : {}),
        ...(typeof v.durationLabel === 'string' ? { durationLabel: v.durationLabel } : {}),
      };
    }
  });
  return out;
};

const _company = types.model('Company', {
  id: types.optional(types.string, ''),
  name: types.maybe(types.string),
  phone: types.maybe(types.string),
  streetAddress: types.maybe(types.string),
  zipCode: types.maybe(types.string),
  city: types.maybe(types.string),
  state: types.maybe(types.string),
  differentiators: types.optional(types.array(types.string), []),
  generalAsks: types.optional(types.array(types.string), []),
  addressableProblems: types.optional(types.array(types.string), []),
  background: types.optional(
    CompanyBackground,
    getSnapshot(NullCompanyBackground)
  ),
  // Plain array, NOT types.union(ProspectProfile, types.array(ProspectProfile)).
  // The union's single-model branch is unreachable (preProcessSnapshot below + the DB
  // always store an array), and once ProspectProfile gained a preProcessSnapshot the
  // union mis-dispatched real arrays into a single empty profile (release-1.8.6 incident).
  prospectProfile: types.optional(types.array(ProspectProfile), []),
  topCompetitors: types.optional(types.array(TopCompetitor), []),
  offerings: types.optional(types.array(CompanyOffering), []),
  questionsAndAnswers: types.optional(types.array(QuestionAnswer), []),
  practiceGoals: types.optional(types.model('PracticeGoals', {
    weeklyTarget: types.optional(types.number, 0),
    monthlyTarget: types.optional(types.number, 0),
  }), {}),
  investorPitchEnabled: types.optional(types.boolean, false),
  liveMeetingsEnabled: types.optional(types.boolean, false),
  // Company leaderboard config (Company-Admin editable via LeaderboardSettings).
  leaderboardEnabled: types.optional(types.boolean, false),
  leaderboardResetCadence: types.optional(
    types.enumeration(['weekly', 'monthly', 'quarterly', 'custom']),
    'monthly'
  ),
  leaderboardCustomStart: types.maybe(types.string),       // ISO date (custom cadence only)
  leaderboardCustomLengthDays: types.maybe(types.number),  // cycle length in days (custom only)
  // Admin-defined leaderboard sub-tables. Each member belongs to at most one group.
  leaderboardGroups: types.optional(types.array(types.model('LeaderboardGroup', {
    id: types.string,
    name: types.string,
    memberIds: types.optional(types.array(types.string), []),
  })), []),
  // Final-standings email config (Company-Admin editable). Groups stored as plain
  // strings (coerced in preProcessSnapshot) so a stray value can't crash company load.
  leaderboardEmailEnabled: types.optional(types.boolean, false),
  leaderboardEmailGroups: types.optional(types.array(types.string), []),
  leaderboardEmailCustom: types.optional(types.array(types.string), []),
  // Scoring difficulty: easy = full points any grade (default); regular = red scores 0;
  // hard = red 0 / yellow full / green 1.5x.
  leaderboardScoringMode: types.optional(types.enumeration(['easy', 'regular', 'hard']), 'easy'),
  // Per-company Training Plan Looms: { intro|elevatorPitch|casualEncounter: { loomId, title?, durationLabel? } }.
  // Multiply-admin editable only (ADMIN_ONLY_FIELDS in backend/libs/patchHandler.js).
  trainingPlanVideos: types.optional(types.frozen(), {}),
})
  .preProcessSnapshot(snapshot => ({
    ...snapshot,
    differentiators: Array.isArray(snapshot.differentiators) ? snapshot.differentiators : [],
    generalAsks: Array.isArray(snapshot.generalAsks) ? snapshot.generalAsks : [],
    addressableProblems: Array.isArray(snapshot.addressableProblems) ? snapshot.addressableProblems : [],
    prospectProfile: Array.isArray(snapshot.prospectProfile) ? snapshot.prospectProfile : [],
    topCompetitors: Array.isArray(snapshot.topCompetitors) ? snapshot.topCompetitors : [],
    offerings: Array.isArray(snapshot.offerings) ? snapshot.offerings : [],
    questionsAndAnswers: Array.isArray(snapshot.questionsAndAnswers) ? snapshot.questionsAndAnswers : [],
    // Coerce an unexpected cadence to 'monthly' so a malformed value can't crash the
    // whole company load (matches the defensive array handling above).
    leaderboardResetCadence: ['weekly', 'monthly', 'quarterly', 'custom'].includes(snapshot.leaderboardResetCadence)
      ? snapshot.leaderboardResetCadence
      : 'monthly',
    // Drop malformed groups so a bad row can't crash the whole company load.
    leaderboardGroups: Array.isArray(snapshot.leaderboardGroups)
      ? snapshot.leaderboardGroups
          .filter(g => g && g.id && g.name)
          .map(g => ({ id: g.id, name: g.name, memberIds: Array.isArray(g.memberIds) ? g.memberIds : [] }))
      : [],
    leaderboardEmailGroups: Array.isArray(snapshot.leaderboardEmailGroups)
      ? snapshot.leaderboardEmailGroups.filter(g => ['companyAdmins', 'executives', 'managers', 'all'].includes(g))
      : [],
    leaderboardEmailCustom: Array.isArray(snapshot.leaderboardEmailCustom)
      ? snapshot.leaderboardEmailCustom.filter(e => typeof e === 'string' && e.trim())
      : [],
    leaderboardScoringMode: ['easy', 'regular', 'hard'].includes(snapshot.leaderboardScoringMode)
      ? snapshot.leaderboardScoringMode
      : 'easy',
    // types.maybe rejects null and wrong types outright — a raw-API write of e.g. a
    // string "14" would otherwise break company load for the whole company (the
    // release-1.8.6 prospect-profile failure class).
    leaderboardCustomStart: typeof snapshot.leaderboardCustomStart === 'string' && snapshot.leaderboardCustomStart
      ? snapshot.leaderboardCustomStart
      : undefined,
    leaderboardCustomLengthDays:
      snapshot.leaderboardCustomLengthDays != null && Number.isFinite(Number(snapshot.leaderboardCustomLengthDays))
        ? Number(snapshot.leaderboardCustomLengthDays)
        : undefined,
    trainingPlanVideos: sanitizeTrainingPlanVideos(snapshot.trainingPlanVideos),
  }));

export const Company = types.compose(_company, ApiStore).actions((self) => {
  function guardCompanyId(capturedId) {
    if (capturedId !== self.id) {
      throw new Error(
        `Company switch detected during save. Expected ${capturedId} but current is ${self.id}. Save aborted.`
      );
    }
  }

  return {
    // chargeForUser removed: its only call site was commented out, the backend
    // handler was a no-op, and the /users/charge route is deleted. Billing is
    // usage-based via calculateUsage.
    createFromSnapshot: flow(function* createFromSnapshot(snapshot) {
      delete snapshot.prospectProfile;
      yield self.postApi('/companies', snapshot, self);
    }),
    updateFromSnapshot: flow(function* updateFromSnapshot(snapshot) {
      const companyId = self.id;
      guardCompanyId(companyId);
      yield self.patchApi(`/companies/${companyId}`, snapshot, self);
    }),
    updateCompanyBackgroundFromSnapshot: flow(
      function* updateCompanyBackgroundFromSnapshot(snapshot) {
        const companyId = self.id;
        self.isLoading = true;
        const file = snapshot.uploadFile;

        if (file) {
          const headers = {
            'Content-Type': file.type,
          };
          guardCompanyId(companyId);
          const result = yield axios.post(
            `/companies/${companyId}/uploads/${file.name}`,
            {},
            { headers }
          );
          const uploadUrl = result.data.uploadUrl;

          yield axios.put(uploadUrl, file, { headers, baseURL: '' });
          snapshot.uploadKey = result.data.key;
          delete snapshot.uploadFile;
        }

        const body = { background: snapshot };

        guardCompanyId(companyId);
        yield self.patchApi(`/companies/${companyId}`, body, self);
      }
    ),
    updateDifferentiatorsFromSnapshot: flow(
      function* updateDifferentiatorsFromSnapshot(differentiatorsSnapshot) {
        const companyId = self.id;
        const body = { differentiators: differentiatorsSnapshot };
        guardCompanyId(companyId);
        yield self.patchApi(`/companies/${companyId}`, body, self);
      }
    ),
    updateGeneralAsksFromSnapshot: flow(
      function* updateGeneralAsksFromSnapshot(generalAsksSnapshot) {
        const companyId = self.id;
        const body = { generalAsks: generalAsksSnapshot };
        guardCompanyId(companyId);
        yield self.patchApi(`/companies/${companyId}`, body, self);
      }
    ),
    updateAddressableProblemsFromSnapshot: flow(
      function* updateAddressableProblemsFromSnapshot(addressableProblemsSnapshot) {
        const companyId = self.id;
        const body = { addressableProblems: addressableProblemsSnapshot };
        guardCompanyId(companyId);
        yield self.patchApi(`/companies/${companyId}`, body, self);
      }
    ),
    updateProspectProfileFromSnapshot: flow(
      function* updateProspectProfileFromSnapshot(targetProfileSnapshot) {
        const companyId = self.id;
        const body = { prospectProfile: targetProfileSnapshot };
        guardCompanyId(companyId);
        yield self.patchApi(`/companies/${companyId}`, body, self);
      }
    ),
    updateTopCompetitorsFromSnapshot: flow(
      function* updateTopCompetitorsFromSnapshot(topCompetitorsSnapshot) {
        const companyId = self.id;
        const body = { topCompetitors: topCompetitorsSnapshot };
        guardCompanyId(companyId);
        yield self.patchApi(`/companies/${companyId}`, body, self);
      }
    ),
    updateOfferingsFromSnapshot: flow(function* updateOfferingsFromSnapshot(
      offeringsSnapshot
    ) {
      const companyId = self.id;
      self.isLoading = true;
      try {
        let index = 0;
        for (let snapshot of offeringsSnapshot) {
          const file = snapshot.uploadFile;

          if (snapshot.uploadFile) {
            const headers = {
              'Content-Type': file.type,
            };
            guardCompanyId(companyId);
            const result = yield axios.post(
              `/companies/${companyId}/uploads/${file.name}`,
              {},
              { headers }
            );
            const uploadUrl = result.data.uploadUrl;

            yield axios.put(uploadUrl, file, { headers, baseURL: '' });
            snapshot.uploadKey = result.data.key;
            delete snapshot.uploadFile;
          } else if (
            self.offerings.length - 1 >= index &&
            self.offerings[index].uploadKey &&
            !snapshot.uploadKey
          ) {
            const key = self.offerings[index].uploadKey.split('/')[1];
            guardCompanyId(companyId);
            yield axios.delete(`/companies/${companyId}/uploads/${key}`);
          }
          index++;
        }

        const body = { offerings: offeringsSnapshot };
        guardCompanyId(companyId);
        yield self.patchApi(`/companies/${companyId}`, body, self);
      } catch (error) {
        console.error(error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),
    updateInvestorPitchEnabled: flow(function* updateInvestorPitchEnabled(enabled) {
      const companyId = self.id;
      const body = { investorPitchEnabled: enabled };
      guardCompanyId(companyId);
      yield self.patchApi(`/companies/${companyId}`, body, self);
    }),
    updateLiveMeetingsEnabled: flow(function* updateLiveMeetingsEnabled(enabled) {
      const companyId = self.id;
      const body = { liveMeetingsEnabled: enabled };
      guardCompanyId(companyId);
      yield self.patchApi(`/companies/${companyId}`, body, self);
    }),
    updateTrainingPlanVideos: flow(function* updateTrainingPlanVideos(videos) {
      const companyId = self.id;
      const body = { trainingPlanVideos: sanitizeTrainingPlanVideos(videos) };
      guardCompanyId(companyId);
      yield self.patchApi(`/companies/${companyId}`, body, self);
    }),
    updateQuestionsAndAnswers: flow(function* updateQuestionsAndAnswers(
      quesionAnswerSnapshot
    ) {
      const companyId = self.id;
      self.isLoading = true;
      try {
        let index = 0;
        for (let snapshot of quesionAnswerSnapshot) {
          const file = snapshot.uploadFile;

          if (snapshot.uploadFile) {
            const headers = {
              'Content-Type': file.type,
            };
            guardCompanyId(companyId);
            const result = yield axios.post(
              `/companies/${companyId}/uploads/${file.name}`,
              {},
              { headers }
            );
            const uploadUrl = result.data.uploadUrl;

            yield axios.put(uploadUrl, file, { headers, baseURL: '' });
            snapshot.uploadKey = result.data.key;
            delete snapshot.uploadFile;
          } else if (
            self.questionsAndAnswers.length - 1 >= index &&
            self.questionsAndAnswers[index].uploadKey &&
            !snapshot.uploadKey
          ) {
            const key = self.questionsAndAnswers[index].uploadKey.split('/')[1];
            guardCompanyId(companyId);
            yield axios.delete(`/companies/${companyId}/uploads/${key}`);
          }
          index++;
        }

        const body = { questionsAndAnswers: quesionAnswerSnapshot };
        guardCompanyId(companyId);
        yield self.patchApi(`/companies/${companyId}`, body, self);
      } catch (error) {
        console.error(error);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),
  };
});

export const NullCompany = Company.create({
  name: '',
  phone: '',
  streetAddress: '',
  zipCode: '',
  city: '',
  state: '',
});
